// frontend/src/services/documentService/templatePdfService.js - Reescrito
import { saveAs } from 'file-saver';
import api from '../api';

const TEMPLATE_TYPES = {};
const MAPPER_FUNCTIONS = {};

/**
 * Servicio mejorado para gestión de plantillas y generación de PDFs
 * Elimina la dependencia de coordenadas hardcodeadas
 */
class TemplatePdfService {
  /**
   * Genera un PDF desde una plantilla HTML usando el servicio de backend
   * @param {Object} options - Opciones de generación
   * @param {string} options.templateId - ID de la plantilla
   * @param {Object} options.data - Datos para rellenar la plantilla
   * @param {Object} options.formData - Datos del formulario (si es un formulario)
   * @param {boolean} options.download - Si debe descargarse automáticamente
   * @param {string} options.fileName - Nombre personalizado del archivo
   * @returns {Promise<Object>} - Información del PDF generado
   */
  static registerTemplate(key, config) {
  TEMPLATE_TYPES[key] = config;
}

static registerMapperFunction(key, fn) {
  MAPPER_FUNCTIONS[key] = fn;
}

static get TEMPLATE_TYPES() {
  return TEMPLATE_TYPES;
}
  static async generatePdf(options) {
    try {
      const {
        templateId,
        data,
        formData,
        download = false,
        fileName
      } = options;
      
      // Enviar solicitud al servidor
      const response = await api.post('/document-templates/generate-pdf', {
        templateId,
        data,
        formData,
        fileName
      });
      
      // Si se solicita descarga automática
      if (download && response.data.url) {
        await this.downloadPdf(response.data.url, fileName || response.data.filename);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error generating PDF from template:', error);
      throw error;
    }
  }
  
  /**
   * Descarga un PDF desde una URL
   * @param {string} url - URL del PDF
   * @param {string} fileName - Nombre del archivo
   * @returns {Promise<void>}
   */
  static async downloadPdf(url, fileName) {
    try {
      // Obtener el archivo como blob
      const response = await fetch(url);
      const blob = await response.blob();
      
      // Usar FileSaver para descargar
      saveAs(blob, fileName);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw error;
    }
  }
  
  /**
   * Genera un PDF de formulario
   * @param {Object} options - Opciones de generación
   * @param {number} options.formularioId - ID del formulario
   * @param {number} options.respuestaId - ID de la respuesta
   * @param {boolean} options.download - Si debe descargarse automáticamente
   * @returns {Promise<Object>} - Información del PDF generado
   */
  static async generateFormPdf(options) {
    try {
      const {
        formularioId,
        respuestaId,
        download = false
      } = options;
      
      // Validar parámetros requeridos
      if (!formularioId && !respuestaId) {
        throw new Error('Se requiere el ID del formulario o de la respuesta');
      }
      
      // Endpoint varía según si tenemos respuestaId o solo formularioId
      const endpoint = respuestaId 
        ? `/formularios/respuestas/${respuestaId}/generar-pdf` 
        : `/formularios/${formularioId}/generar-pdf`;
      
      // Hacer solicitud al servidor
      const response = await api.post(endpoint, { download });
      
      // Si se solicita descarga automática y el servidor no la manejó
      if (download && response.data && response.data.url) {
        await this.downloadPdf(
          response.data.url, 
          response.data.filename || `formulario_${respuestaId || formularioId}.pdf`
        );
      }
      
      return response.data;
    } catch (error) {
      console.error('Error generating form PDF:', error);
      throw error;
    }
  }
  
  /**
   * Genera un documento personalizado desde una plantilla HTML
   * @param {Object} options - Opciones de generación
   * @param {string} options.html - Contenido HTML del documento
   * @param {Object} options.data - Datos para variables en el HTML
   * @param {number} options.membreteId - ID del membrete a usar
   * @param {string} options.fileName - Nombre del archivo
   * @param {boolean} options.download - Si debe descargarse automáticamente
   * @returns {Promise<Object>} - Información del PDF generado
   */
  static async generateHtmlDocument(options) {
    try {
      const {
        html,
        data,
        membreteId,
        fileName,
        download = false
      } = options;
      
      // Enviar solicitud al servidor
      const response = await api.post('/documentos/generar-desde-html', {
        html,
        data,
        membreteId,
        fileName
      });
      
      // Si se solicita descarga automática
      if (download && response.data.url) {
        await this.downloadPdf(
          response.data.url, 
          fileName || response.data.filename
        );
      }
      
      return response.data;
    } catch (error) {
      console.error('Error generating HTML document:', error);
      throw error;
    }
  }
  
  /**
   * Obtiene las plantillas de documentos disponibles
   * @param {Object} filters - Filtros opcionales
   * @returns {Promise<Array>} - Lista de plantillas
   */
  static async getTemplates(filters = {}) {
    try {
      const response = await api.get('/plantillas-documento', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching templates:', error);
      return [];
    }
  }
  
  /**
   * Obtiene los campos de una plantilla específica
   * @param {number} templateId - ID de la plantilla
   * @returns {Promise<Object>} - Estructura de campos
   */
  static async getTemplateFields(templateId) {
    try {
      const response = await api.get(`/plantillas-documento/${templateId}/campos`);
      return response.data;
    } catch (error) {
      console.error('Error fetching template fields:', error);
      throw error;
    }
  }
  
  /**
   * Genera un documento desde plantilla para un paciente/historia
   * @param {Object} options - Opciones para generación
   * @param {number} options.plantillaId - ID de la plantilla
   * @param {number} options.pacienteId - ID del paciente
   * @param {number} options.historiaId - ID de la historia clínica
   * @param {number} options.etapaId - ID de la etapa (opcional)
   * @param {string} options.nombre - Nombre del documento
   * @param {string} options.descripcion - Descripción del documento
   * @returns {Promise<Object>} - Documento generado
   */
  static async generateDocFromTemplate(options) {
    try {
      const {
        plantillaId,
        pacienteId,
        historiaId,
        etapaId,
        nombre,
        descripcion
      } = options;
      
      // Validar parámetros mínimos requeridos
      if (!plantillaId || !pacienteId || !nombre) {
        throw new Error('Faltan parámetros requeridos');
      }
      
      // Enviar solicitud al servidor
      const response = await api.post('/documentos/desde-plantilla', {
        plantilla_id: plantillaId,
        paciente_id: pacienteId,
        historia_clinica_id: historiaId,
        etapa_id: etapaId || null,
        nombre,
        descripcion
      });
      
      return response.data;
    } catch (error) {
      console.error('Error generating document from template:', error);
      throw error;
    }
  }
}

// Exportamos métodos individuales para facilitar su uso
export const generatePdf = TemplatePdfService.generatePdf;
export const generateFormPdf = TemplatePdfService.generateFormPdf;
export const generateHtmlDocument = TemplatePdfService.generateHtmlDocument;
export const getTemplates = TemplatePdfService.getTemplates;
export const getTemplateFields = TemplatePdfService.getTemplateFields;
export const generateDocFromTemplate = TemplatePdfService.generateDocFromTemplate;
export const downloadPdf = TemplatePdfService.downloadPdf;

// También exportamos el servicio completo por defecto
export default TemplatePdfService;